import {
    AdMob,
    BannerAdPosition,
    BannerAdSize,
    RewardAdPluginEvents,
} from '@capacitor-community/admob';
import {
    bannerBottomMargin,
    createAdConsent,
    createInterstitialAds,
    watchBannerMargin,
} from '@gagovictor/shared-frontend-core/dist/ads';

import { config, isNative } from './config';

/** Google's consent form (UMP) for the EEA, UK and Switzerland. Ads start only after it allows them. */
export const adConsent = createAdConsent({ plugin: AdMob, isNative });

/** AdMob is for the mobile builds only: on the web these do nothing (AdSense covers the web). */
const interstitials = createInterstitialAds({
    plugin: AdMob,
    adUnitId: config.admobInterstitialId,
    isTesting: config.admobTesting,
    isNative,
    consent: adConsent,
});

/** Starts the ad SDK. A no-op in the browser. A failure only means no ads are shown. */
export const initializeAdMob = interstitials.initialize;

/** Shows one full-screen ad and always resolves, so a missing ad never traps the person. */
export const showInterstitial = interstitials.show;

/** Shows the footer banner (phone only). Resolves even when no ad loads. */
export const showFooterBanner = async (): Promise<void> => {
    if (!isNative()) return;
    const show = async () => {
        try {
            await initializeAdMob();
            await AdMob.showBanner({
                adId: config.admobBannerId,
                adSize: BannerAdSize.ADAPTIVE_BANNER,
                position: BannerAdPosition.BOTTOM_CENTER,
                margin: bannerBottomMargin(),
                isTesting: config.admobTesting,
            });
        } catch (error) {
            console.warn('Banner could not show:', (error as Error)?.message);
        }
    };
    stopWatchingMargin?.();
    await show();
    stopWatchingMargin = watchBannerMargin(() => void show());
};

let stopWatchingMargin: (() => void) | undefined;

export const hideFooterBanner = async (): Promise<void> => {
    if (!isNative()) return;
    stopWatchingMargin?.();
    stopWatchingMargin = undefined;
    try {
        await AdMob.removeBanner();
    } catch {
        /* nothing to remove */
    }
};

/**
 * - rewarded: the video was watched to the end;
 * - dismissed: it was closed early;
 * - unavailable: no ad could be loaded or shown (offline, no fill, no unit configured). The caller lets the
 *   person through, so a missing ad never traps them.
 */
export type RewardedResult = 'rewarded' | 'dismissed' | 'unavailable';

/** Loads and shows one rewarded video. Always resolves. */
export const showRewardedVideo = async (): Promise<RewardedResult> => {
    if (!isNative() || !config.admobRewardedId) return 'unavailable';
    await initializeAdMob();
    const handles: { remove: () => Promise<void> | void }[] = [];
    try {
        return await new Promise<RewardedResult>((resolve) => {
            let rewarded = false;
            Promise.all([
                AdMob.addListener(RewardAdPluginEvents.Rewarded, () => {
                    rewarded = true;
                }),
                AdMob.addListener(RewardAdPluginEvents.Dismissed, () =>
                    resolve(rewarded ? 'rewarded' : 'dismissed')
                ),
                AdMob.addListener(RewardAdPluginEvents.FailedToLoad, () =>
                    resolve('unavailable')
                ),
                AdMob.addListener(RewardAdPluginEvents.FailedToShow, () =>
                    resolve('unavailable')
                ),
            ])
                .then(async (listeners) => {
                    handles.push(...listeners);
                    await AdMob.prepareRewardVideoAd({
                        adId: config.admobRewardedId,
                        isTesting: config.admobTesting,
                    });
                    await AdMob.showRewardVideoAd();
                })
                .catch(() => resolve('unavailable'));
        });
    } finally {
        await Promise.all(
            handles.map((h) =>
                Promise.resolve(h.remove()).catch(() => undefined)
            )
        );
    }
};
